import { MultiAllocationSheetData, RatioSheetKind } from 'src/models/multidimensional-allocation/MultiAllocationSheetData';
import { DerivedAllocationSheetData } from 'src/models/derived-allocation-sheet/DerivedAllocationSheet';
import CONSTANTS from 'src/utils/constants';

/** Kind of a sheet; absent means an uploaded sheet, as on every data source saved before derived sheets existed. */
export const kindOf = (kind?: string | null): RatioSheetKind => kind === 'DERIVED' ? 'DERIVED' : 'STANDARD';

/** Labels shown on each picker option. */
export const RATIO_SHEET_KIND_LABEL: Record<RatioSheetKind, string> = {
    STANDARD: 'Uploaded',
    DERIVED: 'Derived',
};

/**
 * Picker value. Uploaded and derived sheet ids are unique only within their own table, so the kind is part of the
 * value.
 */
export const sheetOptionValue = (kind: RatioSheetKind | string | undefined | null, sheetId: string): string =>
    `${kindOf(kind)}#${sheetId}`;

/** Normalises a segment label ("Cost Center") or enum name ("COST_CENTER") to the enum name. */
export const normaliseSegment = (segment?: string | null): string =>
    (segment ?? '').toUpperCase().trim().replace(/\s+/g, '_');

const isActive = (status?: string | null): boolean => normaliseSegment(status) === 'ACTIVE';

/**
 * Turns the derived-sheet list into picker rows. Only active sheets with a segment can give a data source its
 * percentages. A derived sheet is always followed at its live version, so its version is 0.
 */
export const toDerivedPickerSheets = (sheets: DerivedAllocationSheetData[] | null | undefined): MultiAllocationSheetData[] =>
    (sheets ?? [])
        .filter(sheet => isActive(sheet.status as unknown as string) && !!sheet.segmentType)
        .map(sheet => ({
            sheetId: sheet.sheetId,
            sheetName: sheet.sheetName,
            sheetVersion: 0,
            segmentType: sheet.segmentType as string,
            description: sheet.description,
            lastUpdatedBy: sheet.lastModifiedBy,
            lastUpdatedAt: sheet.lastModifiedDate,
            status: sheet.status,
            createdAt: sheet.createdAt,
            createdBy: sheet.createdBy,
            kind: 'DERIVED' as RatioSheetKind,
        }));

/** Uploaded sheets first, then derived ones, each tagged with its kind. */
export const mergePickerSheets = (uploaded: MultiAllocationSheetData[] | null | undefined,
                                  derived: MultiAllocationSheetData[] | null | undefined): MultiAllocationSheetData[] => [
    ...(uploaded ?? []).map(sheet => ({ ...sheet, kind: 'STANDARD' as RatioSheetKind })),
    ...(derived ?? []),
];

/** Finds the picker row a data source points at. */
export const findPickerSheet = (sheets: MultiAllocationSheetData[] | null | undefined, sheetId?: string,
                                kind?: string | null): MultiAllocationSheetData | undefined =>
    sheetId ? (sheets ?? []).find(sheet => sheet.sheetId === sheetId && kindOf(sheet.kind) === kindOf(kind)) : undefined;

/** One picker option: the sheet name, tagged Uploaded or Derived. */
export const toSheetOption = (sheet: MultiAllocationSheetData) => ({
    label: sheet.sheetName,
    value: sheetOptionValue(sheet.kind, sheet.sheetId),
    tags: [RATIO_SHEET_KIND_LABEL[kindOf(sheet.kind)]],
});

/**
 * The sheets the picker offers a data source: those on its segment, when it has one. Filtering on the data source's
 * own segment keeps the list right when a saved data source is opened, and when the derived-sheet list arrives after
 * the segment was chosen.
 */
export const sheetsForSegment = (sheets: MultiAllocationSheetData[] | null | undefined,
                                 segment?: string | null): MultiAllocationSheetData[] => {
    const wanted = normaliseSegment(segment);
    return (sheets ?? []).filter(sheet => !wanted || normaliseSegment(sheet.segmentType) === wanted);
};

/**
 * The ratioSheetKind a picked sheet saves with. While DAS in MDA is on, every pick names its kind, so a data source
 * moved off a Derived Allocation Sheet to an uploaded one is saved STANDARD: a WebAPI that keeps the stored kind when
 * none is sent would otherwise leave it DERIVED. With the flag off an uploaded pick sends no kind, exactly as before
 * derived sheets existed, so rows and promotion hashes do not change.
 */
export const kindToSave = (kind: string | null | undefined, dasInMdaEnabled: boolean): RatioSheetKind | undefined => {
    if (kindOf(kind) === 'DERIVED') {
        return 'DERIVED';
    }
    return dasInMdaEnabled ? 'STANDARD' : undefined;
};

/**
 * Sheet names by picker value, so a sheet the picker does not offer can still be named. Covers derived sheets of any
 * status and active uploaded sheets.
 */
export const sheetNamesByValue = (uploaded: MultiAllocationSheetData[] | null | undefined,
                                  derived: DerivedAllocationSheetData[] | null | undefined): Map<string, string> =>
    new Map([
        ...(uploaded ?? []).map(sheet => [sheetOptionValue('STANDARD', sheet.sheetId), sheet.sheetName] as [string, string]),
        ...(derived ?? []).map(sheet => [sheetOptionValue('DERIVED', sheet.sheetId), sheet.sheetName] as [string, string]),
    ].filter(([, name]) => !!name));

/**
 * The option shown as selected. A saved sheet that is not in the list (a derived sheet with an open draft, a failed
 * list, the flag off) is still shown by name, marked unavailable in its label so the closed picker shows it too.
 * While the lists load, it is shown by name or id only.
 */
export const selectedSheetOption = (options: ReturnType<typeof toSheetOption>[], sheetId?: string,
                                    kind?: string | null, sheetNames?: Map<string, string>, loading = false) => {
    if (!sheetId) {
        return null;
    }
    const value = sheetOptionValue(kind, sheetId);
    const kindLabel = RATIO_SHEET_KIND_LABEL[kindOf(kind)];
    const name = sheetNames?.get(value) ?? sheetId;
    const missing = loading
        ? { label: name, value, tags: [kindLabel] }
        : { label: `${name} (${kindLabel}, not available)`, value, tags: [kindLabel, 'Not available'] };
    return options.find(option => option.value === value) ?? missing;
};

/** The page that shows the sheet a data source points at, or null when none is chosen. */
export const sheetDetailsUrl = (sheetId?: string, version?: number | null, kind?: string | null): string | null => {
    if (!sheetId) {
        return null;
    }
    if (kindOf(kind) === 'DERIVED') {
        return `${CONSTANTS.PAGE_NAV.MULTIDIMENSIONAL_ALLOCATION_DERIVED_SHEET_DETAILS.URL}/${sheetId}`;
    }
    return version != null
        ? `${CONSTANTS.PAGE_NAV.MULTIDIMENSIONAL_ALLOCATION_SHEET_DETAILS.URL}/${sheetId}?version=${version}`
        : null;
};
